import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageBanner } from "@/components/ui/PageBanner";
import { IMG } from "@/lib/assets";
import { SCORE_TYPES, today, addDays, prettyDay } from "@/lib/perform";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Plus, Trash2, Medal, Users, ChevronRight } from "lucide-react";

const field = "h-10 rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 text-sm w-full";
const PLACE = ["#FFD60A", "#C0C7D4", "#CD7F32"];

function CreateComp({ open, onOpenChange, onDone }) {
  const [f, setF] = useState({ name: "", date: addDays(today(), 14), description: "", divisions: "Rx Men, Rx Women, Scaled Men, Scaled Women",
    events: [{ title: "Event 1", description: "", score_type: "time" }] });
  const save = async () => {
    try {
      await api.post("/api/comps", { ...f, divisions: f.divisions.split(",").map((d) => d.trim()).filter(Boolean) });
      toast.success("Competition created"); onDone(); onOpenChange(false);
    } catch (err) { toast.error(apiErr(err)); }
  };
  const setE = (i, k, v) => setF({ ...f, events: f.events.map((e, j) => (j === i ? { ...e, [k]: v } : e)) });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="surface border-[#1E222A] max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">New in-house competition</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input className={`${field} md:col-span-2`} placeholder="Name, e.g. Autumn Throwdown" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="comp-name" />
            <input className={field} type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
          </div>
          <input className={field} placeholder="Divisions, separated by commas" value={f.divisions} onChange={(e) => setF({ ...f, divisions: e.target.value })} />
          <textarea className="rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 py-2 text-sm w-full" rows={2} placeholder="Description, schedule, standards" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
          {f.events.map((e, i) => (
            <div key={i} className="rounded-xl border border-[#1E222A] p-3 space-y-2">
              <div className="flex gap-2">
                <input className={field} placeholder={`Event ${i + 1} title`} value={e.title} onChange={(x) => setE(i, "title", x.target.value)} />
                <select className={`${field} w-48`} value={e.score_type} onChange={(x) => setE(i, "score_type", x.target.value)}>
                  {SCORE_TYPES.filter((s) => s.key !== "none").map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
                </select>
                <button onClick={() => setF({ ...f, events: f.events.filter((_, j) => j !== i) })} className="p-2 text-[#8A91A6] hover:text-pulseError" aria-label="Remove event"><Trash2 className="w-4 h-4" /></button>
              </div>
              <textarea className="rounded-lg bg-[#0B0D11] border border-[#1E222A] px-3 py-2 text-sm w-full" rows={2} placeholder="Workout" value={e.description} onChange={(x) => setE(i, "description", x.target.value)} />
            </div>
          ))}
          <button onClick={() => setF({ ...f, events: [...f.events, { title: `Event ${f.events.length + 1}`, description: "", score_type: "time" }] })} className="text-sm text-acid inline-flex items-center gap-1"><Plus className="w-4 h-4" /> Add event</button>
          <button onClick={save} className="w-full h-11 rounded-full bg-acid text-black font-medium" data-testid="comp-save">Create competition</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScoreEntry({ comp, ev, athleteId, onDone }) {
  const [v, setV] = useState({});
  const st = comp.events[ev].score_type;
  const n = (x) => (x === "" || x === undefined ? null : Number(x));
  const save = async () => {
    try {
      await api.post(`/api/comps/${comp.id}/events/${ev}/score`, { user_id: athleteId || null, time: v.time, rounds: n(v.rounds), reps: n(v.reps), load_kg: n(v.load_kg), calories: n(v.calories), distance_m: n(v.distance_m) });
      toast.success("Score saved"); setV({}); onDone();
    } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="flex flex-wrap gap-2 items-center">
      {st === "time" && <input className={`${field} w-32`} placeholder="mm:ss" value={v.time || ""} onChange={(e) => setV({ ...v, time: e.target.value })} />}
      {st === "amrap" && <><input className={`${field} w-24`} type="number" placeholder="Rounds" onChange={(e) => setV({ ...v, rounds: e.target.value })} /><input className={`${field} w-24`} type="number" placeholder="+ reps" onChange={(e) => setV({ ...v, reps: e.target.value })} /></>}
      {st === "load" && <input className={`${field} w-32`} type="number" placeholder="kg" onChange={(e) => setV({ ...v, load_kg: e.target.value })} />}
      {["reps", "rounds", "calories", "distance"].includes(st) && <input className={`${field} w-32`} type="number" placeholder={st} onChange={(e) => setV({ ...v, [st === "distance" ? "distance_m" : st]: e.target.value })} />}
      <button onClick={save} className="h-10 px-4 rounded-full bg-acid text-black text-sm font-medium">Save</button>
    </div>
  );
}

function CompDetail({ id, onBack }) {
  const qc = useQueryClient();
  const { data: c } = useQuery({ queryKey: ["comp", id], queryFn: async () => (await api.get(`/api/comps/${id}`)).data });
  const [div, setDiv] = useState(null);
  const [regDiv, setRegDiv] = useState("");
  if (!c) return <p className="mono-label animate-pulse">loading…</p>;
  const d = div || c.my_registration?.division || c.divisions[0];
  const table = c.leaderboard[d] || [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["comp", id] });
  const register = async () => {
    try { await api.post(`/api/comps/${id}/register`, { division: regDiv || c.divisions[0] }); toast.success("You're in!"); refresh(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="space-y-5" data-testid="comp-detail">
      <button onClick={onBack} className="text-sm text-[#8A91A6] hover:text-white">All competitions</button>
      <div className="relative overflow-hidden rounded-2xl border border-[#FFD60A]/30 bg-gradient-to-br from-[#1C1806] to-[#0B0D11] p-6">
        <Trophy className="absolute right-6 top-6 w-20 h-20 text-[#FFD60A]/15" />
        <p className="mono-label text-[#FFD60A]">{prettyDay(c.date)} · {c.athletes} athletes</p>
        <p className="font-heading font-black text-3xl mt-1">{c.name}</p>
        {c.description && <p className="text-sm text-[#B7BECB] mt-2 max-w-2xl whitespace-pre-line">{c.description}</p>}
        {!c.my_registration ? (
          <div className="flex flex-wrap gap-2 mt-4">
            <select className={`${field} w-48`} value={regDiv} onChange={(e) => setRegDiv(e.target.value)}>{c.divisions.map((x) => <option key={x}>{x}</option>)}</select>
            <button onClick={register} className="h-10 px-5 rounded-full bg-[#FFD60A] text-black font-medium" data-testid="comp-register">Register</button>
          </div>
        ) : <p className="mt-3 text-sm text-acid">You're registered in {c.my_registration.division}.</p>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {c.events.map((e) => (
          <div key={e.index} className="surface rounded-2xl p-4">
            <p className="mono-label">event {e.index + 1} · {SCORE_TYPES.find((s) => s.key === e.score_type)?.label}</p>
            <p className="font-heading font-bold text-lg">{e.title}</p>
            {e.description && <p className="text-sm text-[#B7BECB] whitespace-pre-line mt-1">{e.description}</p>}
            {c.my_registration && <div className="mt-3"><p className="text-xs text-[#8A91A6] mb-1">Your score</p><ScoreEntry comp={c} ev={e.index} onDone={refresh} /></div>}
          </div>
        ))}
      </div>
      <div className="surface rounded-2xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <p className="font-heading font-bold flex items-center gap-2"><Medal className="w-4 h-4 text-[#FFD60A]" /> Leaderboard</p>
          <div className="flex flex-wrap gap-1.5">{c.divisions.map((x) => <button key={x} onClick={() => setDiv(x)} className={`mono-label rounded-full px-3 h-7 border ${d === x ? "bg-[#FFD60A] text-black border-[#FFD60A]" : "border-[#343A4A] text-[#8A91A6]"}`}>{x}</button>)}</div>
        </div>
        <p className="text-xs text-[#565C6F] mb-2">Points = your place in each event, added up. Lowest total wins.</p>
        {table.length === 0 ? <p className="text-sm text-[#8A91A6]">No athletes in this division yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left mono-label"><th className="py-2 pr-2">#</th><th className="pr-2">athlete</th>{c.events.map((e) => <th key={e.index} className="pr-2">E{e.index + 1}</th>)}<th className="text-right">points</th></tr></thead>
              <tbody>
                {table.map((a) => (
                  <tr key={a.place} className={`border-t border-[#1E222A] ${a.mine ? "text-acid" : ""}`}>
                    <td className="py-2 pr-2 font-black" style={{ color: PLACE[a.place - 1] }}>{a.place}</td>
                    <td className="pr-2">{a.name}</td>
                    {c.events.map((e) => { const r = a.events[String(e.index)]; return <td key={e.index} className="pr-2 font-mono2">{r ? `${r.display} (${r.rank})` : "–"}</td>; })}
                    <td className="text-right font-mono2 font-bold">{a.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Competitions() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const staff = ["owner", "coach"].includes(user?.club_role) && user?.club_active;
  const [create, setCreate] = useState(false);
  const [open, setOpen] = useState(null);
  const { data = [] } = useQuery({ queryKey: ["comps"], queryFn: async () => (await api.get("/api/comps")).data });
  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.strength} overline="competitions" title="Throwdowns"
        subtitle="In-house competitions with divisions, events and a live points leaderboard."
        actions={staff && <button onClick={() => setCreate(true)} className="inline-flex items-center gap-2 rounded-full h-10 px-5 bg-acid text-black font-medium" data-testid="comp-new"><Plus className="w-4 h-4" /> New competition</button>} />
      {open ? <CompDetail id={open} onBack={() => setOpen(null)} /> : (
        <>
          {!user?.club_id && <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">Competitions are run by clubs. Join your club to take part.</p>}
          {user?.club_id && data.length === 0 && <p className="text-sm text-[#8A91A6] surface rounded-2xl p-6 text-center">No competitions yet. {staff ? "Create your first throwdown." : "Your coach will post them here."}</p>}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.map((c, i) => (
              <motion.button key={c.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} onClick={() => setOpen(c.id)}
                className="text-left relative overflow-hidden rounded-2xl border border-[#1E222A] bg-[#0F1115] p-5 hover:border-[#FFD60A]/50 group">
                <Trophy className="absolute -right-4 -bottom-4 w-28 h-28 text-[#FFD60A]/10 group-hover:text-[#FFD60A]/20 transition-colors" />
                <p className="mono-label text-[#FFD60A]">{c.date} · {c.events.length} events</p>
                <p className="font-heading font-black text-2xl mt-1">{c.name}</p>
                <p className="text-sm text-[#8A91A6] mt-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.athletes} athletes {c.registered && <span className="text-acid ml-2">you're in</span>}</p>
                <ChevronRight className="absolute right-5 top-5 w-5 h-5 text-[#565C6F]" />
              </motion.button>
            ))}
          </div>
        </>
      )}
      <CreateComp open={create} onOpenChange={setCreate} onDone={() => qc.invalidateQueries({ queryKey: ["comps"] })} />
    </div>
  );
}
